import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, CircularProgress, Stack } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import { useNavigate } from '@tanstack/react-router';

import { isAuth0Initialized, startLogin } from '../lib/auth0-client';
import { FEDERATED_CONNECTIONS, getSingleAutoConnection } from '../lib/auth0-connections';
import { useAuthTranslation } from '../i18n/auth-i18n';
import { readQueryParam, safeRedirectPath } from '../lib/query-params';
import { AUTH_PATHS } from '../lib/auth-paths';
import { AuthLayout } from './AuthLayout';

export interface LoginPageProps {
  /** Reactive auth flag from the app store; drives the post-login redirect. */
  isAuthenticated: boolean;
  /**
   * The backend reports the session cookie holds a logout tombstone, so the
   * user signed out on purpose. The automatic handoff still runs, but it
   * forces a fresh Auth0 prompt instead of a silent session reuse.
   */
  explicitLogout: boolean;
  logo: React.ReactElement;
  /** Brand subtitle below the logo. Omit to show the logo alone. */
  brandingSubtitle?: string;
}

const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';

const SOCIAL_BUTTON_SX = (theme: Theme) => ({
  height: 44,
  borderRadius: '8px',
  borderColor: 'divider',
  color: 'text.primary',
  fontWeight: 500,
  textTransform: 'none',
  transition: `transform 160ms ${EASE_OUT}, border-color 200ms ${EASE_OUT}, background-color 200ms ${EASE_OUT}`,
  '&:hover': { borderColor: alpha(theme.palette.common.white, 0.3), backgroundColor: 'action.hover' },
  '&:active': { transform: 'scale(0.96)' },
  '&.Mui-disabled': { transform: 'none' },
});

/**
 * Federated sign-in: auto-redirects on mount when exactly one connection is
 * configured, forcing a fresh Auth0 prompt after a logout or an IDP error so
 * the bounce never silently reuses a live session. Only a failure to start
 * the handoff itself, or more than one configured connection, shows a manual
 * button instead.
 */
export function LoginPage({ isAuthenticated, explicitLogout, logo, brandingSubtitle }: LoginPageProps) {
  const { t } = useAuthTranslation();
  const navigate = useNavigate();
  const [redirecting, setRedirecting] = useState<string | null>(null);
  // Honour a guarded ?redirect= (the deep-link captured by the protected route's
  // beforeLoad) so post-login lands on the requested page, not just '/'.
  const [redirectTo] = useState(() => safeRedirectPath(readQueryParam('redirect')) ?? '/');
  // The callback appends its own `#auth=` / `?error=`, so only the path survives.
  const redirectPath = redirectTo.split(/[?#]/)[0];
  // Read before AuthLayout strips it. Determines forceLogin below, not whether to bounce.
  // The protected route's beforeLoad nests a callback error inside `redirect=`,
  // so `error` is rarely a top-level param here — check both places.
  const [errorCode] = useState<string | null>(() => {
    const topLevel = readQueryParam('error');
    if (topLevel !== null) return topLevel;
    const nestedSearch = redirectTo.split(/[?#]/)[1];
    return nestedSearch ? new URLSearchParams(nestedSearch).get('error') : null;
  });
  const hasAuthError = errorCode !== null;
  // access_denied means the IDP explicitly rejected the user (no workspace, kill
  // switch, …) — auto-bouncing would silently repeat the same denial. Every other
  // mapped error is transient (state mismatch, network) and safe to retry automatically.
  const isAccessDenied = errorCode === 'access_denied';
  // The IDP session outlives our logout, so an auto-handoff would sign the user
  // straight back in. Read once at mount so a later change cannot re-arm it.
  const [hadExplicitLogout] = useState(explicitLogout);
  // Latch so StrictMode's double-invoke cannot fire startLogin twice.
  const autoStarted = useRef(false);
  // The handoff is one-shot: without this a failure leaves the spinner up forever.
  const [handoffFailed, setHandoffFailed] = useState(false);
  // Auto-redirect only makes sense with exactly one option; two or more need a picker.
  const autoConnection = getSingleAutoConnection(FEDERATED_CONNECTIONS);
  // Only a failure to start the handoff blocks the auto-bounce — an IDP error or a
  // prior logout still bounce, just with a forced prompt (see forceLogin below).
  const federatedHandoff = autoConnection !== null && !handoffFailed && !isAccessDenied;
  // A live session must not answer the bounce silently after a logout or an
  // IDP error — force Auth0 to show its own login prompt instead.
  const forceLogin = hadExplicitLogout || hasAuthError;

  // Reactive redirect: when the store flips authenticated (post-login or a
  // pre-existing session), leave the login page for the intended destination.
  useEffect(() => {
    if (isAuthenticated) navigate({ to: redirectTo });
  }, [isAuthenticated, navigate, redirectTo]);

  const handleConnectionClick = useCallback(
    (connection: string, forceLogin?: boolean) => {
      if (!isAuth0Initialized()) {
        setHandoffFailed(true);
        console.error('[@nexus/supabase] Auth0 not initialized');
        return;
      }
      // Not cleared here: only a completed sign-in lifts it, so a failed IDP round
      // trip still shows the button.
      setRedirecting(connection);
      try {
        startLogin(connection, `${window.location.origin}${redirectPath}`, undefined, forceLogin);
      } catch (err) {
        setRedirecting(null);
        setHandoffFailed(true);
        console.error('[@nexus/supabase] Failed to start social login', err);
      }
    },
    [redirectPath]
  );

  const handleReturnToSignIn = useCallback(() => {
    navigate({ to: AUTH_PATHS.login, replace: true });
    if (autoConnection) handleConnectionClick(autoConnection.connection, true);
  }, [navigate, autoConnection, handleConnectionClick]);

  // Hand off on mount. The latch and isAuthenticated guard stop repeat invokes.
  useEffect(() => {
    if (!federatedHandoff || isAuthenticated || autoStarted.current || !autoConnection) return;
    autoStarted.current = true;
    handleConnectionClick(autoConnection.connection, forceLogin);
  }, [federatedHandoff, isAuthenticated, handleConnectionClick, autoConnection, forceLogin]);

  // While the handoff runs, show only a spinner.
  if (federatedHandoff && autoConnection) {
    return (
      <AuthLayout logo={logo} brandingSubtitle={brandingSubtitle} data-testid="auth.login-page" errorCode={errorCode}>
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress aria-label={t(autoConnection.labelKey)} />
        </Stack>
      </AuthLayout>
    );
  }

  // Handoff itself failed, or more than one connection: manual picker only.
  // A real picker click never forces login — the user's click is itself the explicit
  // action. A single-connection retry after a failed forced auto-bounce still needs
  // forceLogin, since the reason for the force (logout/error) has not changed.
  return (
    <AuthLayout logo={logo} brandingSubtitle={brandingSubtitle} data-testid="auth.login-page" errorCode={errorCode}>
      {handoffFailed && (
        <Alert severity="error" sx={{ mb: 2.5 }}>
          {t('auth.errors.oauthFailed')}
        </Alert>
      )}
      {isAccessDenied && (
        <Button
          fullWidth
          variant="outlined"
          data-testid="auth.login-page.return-to-sign-in"
          onClick={handleReturnToSignIn}
          sx={SOCIAL_BUTTON_SX}
        >
          {t('auth.login.returnToSignIn')}
        </Button>
      )}
      {!isAccessDenied && (
        <Stack spacing={1.5}>
          {FEDERATED_CONNECTIONS.map((option) => (
            <Button
              key={option.connection}
              fullWidth
              variant="outlined"
              disabled={redirecting !== null}
              startIcon={
                redirecting !== option.connection ? <Icon icon={option.icon} width={20} height={20} /> : undefined
              }
              onClick={() => handleConnectionClick(option.connection, autoConnection ? forceLogin : undefined)}
              sx={SOCIAL_BUTTON_SX}
            >
              {redirecting === option.connection ? <CircularProgress size={20} color="inherit" /> : t(option.labelKey)}
            </Button>
          ))}
        </Stack>
      )}
    </AuthLayout>
  );
}
